import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeElementsOptions } from '@stripe/stripe-js';
import { getStripe, stripeConfigured } from '../lib/stripe';
import {
  createSetupIntent,
  fetchMyPaymentMethod,
  fetchExistingCard,
  useExistingCardForBidding,
  fetchMyBidContact,
  saveBidContact,
} from '../lib/queries';
import { supabase } from '../lib/supabase';
import { useSession } from '../lib/session';
import { COUNTRIES, flagEmoji } from '../lib/countries';
import { DIAL_CODES, buildE164 } from '../lib/dial-codes';
import './AddCardModal.css';

/**
 * "Add a card to bid" modal. Two paths, both gated behind a plain-language
 * acknowledgement that winning means being charged off-session:
 *   • Reuse — if the member already has a card on their Stripe Customer (e.g.
 *     from their subscription), offer one-click "use my card on file".
 *   • New card — collect one via Stripe Elements (Payment Element) on a
 *     SetupIntent. Card data stays inside Stripe's iframe.
 *
 * Stripe.js is loaded lazily (only when this modal mounts), so it isn't pulled
 * in app-wide. On success the webhook flips has_card_on_file; we briefly poll
 * for it so the bid panel unlocks without a manual refresh, then call onLinked().
 */

const ACK_TEXT =
  "I understand that if I win an auction, Converted Closet will charge this card for the " +
  "winning bid (plus any shipping or fees) after the auction closes, without needing me to " +
  "confirm again.";

const CONSENT_TEXT =
  "I agree to receive updates about the lots I bid on — outbid alerts, results, and payment — " +
  "by email and SMS. This is only for my own bidding activity; it is not marketing and I won't " +
  "be signed up for anything else.";

async function waitForCardOnFile() {
  for (let i = 0; i < 8; i++) {
    try {
      const pm = await fetchMyPaymentMethod();
      if (pm?.has_card_on_file) return;
    } catch { /* transient — keep polling */ }
    await new Promise((r) => setTimeout(r, 1500));
  }
}

export function AddCardModal({ onClose, onLinked }: { onClose: () => void; onLinked: () => void }) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const { profile } = useSession();
  // Lazy: Stripe.js loads only now, when the modal is actually open.
  const stripePromise = useMemo(() => getStripe(), []);

  const [mode, setMode] = useState<'loading' | 'register' | 'choose' | 'new'>('loading');
  const [existingCard, setExistingCard] = useState<{ brand: string | null; last4: string | null } | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Prefill for the register step (name from profile, email from the account).
  const [defaults, setDefaults] = useState<{ name: string; email: string }>({ name: '', email: '' });

  // Body scroll lock + Esc close (mirrors SubmissionModal).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') { e.preventDefault(); onClose(); } }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const startNewCard = useCallback(() => {
    setMode('new');
    setClientSecret((cs) => {
      if (cs) return cs;
      createSetupIntent()
        .then(setClientSecret)
        .catch((err) => setLoadError(err?.message ?? 'Could not start card setup.'));
      return null;
    });
  }, []);

  // Move from the register step into the card step: reuse a card if one already
  // exists on the Stripe Customer, else go straight to the new-card form.
  const proceedToCard = useCallback(() => {
    setMode('loading');
    fetchExistingCard()
      .then((card) => {
        if (card?.last4) { setExistingCard(card); setMode('choose'); }
        else startNewCard();
      })
      .catch(() => startNewCard());
  }, [startNewCard]);

  // On open: register first (collect contact + consent) unless they've already
  // registered — then move on to the card step.
  useEffect(() => {
    let cancelled = false;
    if (!stripeConfigured) { setLoadError('Card payments are not available right now.'); return; }
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      const email = sess.session?.user?.email ?? '';
      if (cancelled) return;
      setDefaults({ name: profile?.display_name ?? '', email });
      try {
        const contact = await fetchMyBidContact();
        if (cancelled) return;
        if (contact) proceedToCard();
        else setMode('register');
      } catch {
        if (!cancelled) setMode('register');
      }
    })();
    return () => { cancelled = true; };
  }, [proceedToCard, profile?.display_name]);

  const onBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  async function finish() {
    await waitForCardOnFile();
    onLinked();
    onClose();
  }

  const options: StripeElementsOptions | undefined = clientSecret
    ? { clientSecret, appearance: { theme: 'flat', variables: { borderRadius: '10px' } } }
    : undefined;

  return (
    <div ref={overlayRef} className="addcard" role="dialog" aria-modal="true" aria-label="Register to bid" onClick={onBackdropClick}>
      <div className="addcard__panel">
        <button type="button" className="addcard__close" onClick={onClose} aria-label="Close">×</button>
        <p className="kicker addcard__kicker">Sales Room</p>
        <h2 className="addcard__title">{mode === 'register' ? 'Register to bid' : 'Add a card to bid'}</h2>
        <p className="addcard__lede">
          {mode === 'register'
            ? 'Two quick steps to bid: your contact details, then a card on file. You’re only charged if you win.'
            : 'Bidding is a commitment, so we keep a card on file. You’re only charged if you win: the winning bid is settled after the auction closes.'}
        </p>

        {loadError && <p className="addcard__error" role="alert">{loadError}</p>}

        {!loadError && mode === 'loading' && <p className="addcard__loading">Loading…</p>}

        {!loadError && mode === 'register' && (
          <RegisterStep defaults={defaults} onSaved={proceedToCard} onClose={onClose} />
        )}

        {!loadError && mode === 'choose' && existingCard && (
          <ReuseChoice card={existingCard} onUseExisting={finish} onDifferent={startNewCard} onClose={onClose} />
        )}

        {!loadError && mode === 'new' && !options && <p className="addcard__loading">Loading secure card form…</p>}

        {!loadError && mode === 'new' && options && (
          <Elements stripe={stripePromise} options={options}>
            <CardForm onClose={onClose} onDone={finish} />
          </Elements>
        )}
      </div>
    </div>
  );
}

/** Step 1 of registering to bid: contact details + explicit consent to receive
 *  bid-activity updates. Saved before the card step. */
function RegisterStep({
  defaults, onSaved, onClose,
}: {
  defaults: { name: string; email: string };
  onSaved: () => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(defaults.name);
  const [phoneCountry, setPhoneCountry] = useState('US');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(defaults.email);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valid = name.trim() !== '' && phone.trim() !== '' && email.trim() !== '' && consent;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid || busy) return;
    // SMS alerts need a real E.164 number — country code from the selector
    // plus the national number (a pasted +… number wins over the selector).
    const normalizedPhone = buildE164(phoneCountry, phone);
    if (!normalizedPhone) {
      setError('That doesn’t look like a mobile number — check the country and the number.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveBidContact({ full_name: name.trim(), phone: normalizedPhone, email: email.trim(), consent: true });
      onSaved();
    } catch (err: any) {
      setError(err?.message ?? 'Could not save your details. Try again.');
      setBusy(false);
    }
  }

  return (
    <form className="addcard__form" onSubmit={submit}>
      <label className="addcard__field">
        <span className="addcard__field-label">Name</span>
        <input className="addcard__input" type="text" autoComplete="name" value={name}
               onChange={(e) => setName(e.target.value)} disabled={busy} required />
      </label>
      <div className="addcard__field">
        <span className="addcard__field-label">Mobile number</span>
        <div className="addcard__phone-row">
          <select
            className="addcard__input addcard__phone-country"
            value={phoneCountry}
            onChange={(e) => setPhoneCountry(e.target.value)}
            disabled={busy}
            aria-label="Phone country"
          >
            {COUNTRIES.filter((c) => DIAL_CODES[c.code]).map((c) => (
              <option key={c.code} value={c.code}>
                {flagEmoji(c.code)} {c.name} (+{DIAL_CODES[c.code]})
              </option>
            ))}
          </select>
          <input className="addcard__input addcard__phone-number" type="tel" inputMode="tel"
                 autoComplete="tel-national" value={phone} aria-label="Mobile number"
                 onChange={(e) => setPhone(e.target.value)} disabled={busy} required
                 placeholder="For bid updates by SMS" />
        </div>
      </div>
      <label className="addcard__field">
        <span className="addcard__field-label">Email</span>
        <input className="addcard__input" type="email" autoComplete="email" value={email}
               onChange={(e) => setEmail(e.target.value)} disabled={busy} required />
      </label>

      <label className="addcard__ack">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
               aria-label="Consent to bid-activity updates" />
        <span>{CONSENT_TEXT}</span>
      </label>

      {error && <p className="addcard__error" role="alert">{error}</p>}

      <div className="addcard__actions">
        <button type="button" className="btn btn--ghost btn--md" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn--primary btn--md" disabled={!valid || busy}>
          {busy ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </form>
  );
}

/** One-click reuse of a card already on the member's Stripe Customer. */
function ReuseChoice({
  card, onUseExisting, onDifferent, onClose,
}: {
  card: { brand: string | null; last4: string | null };
  onUseExisting: () => Promise<void>;
  onDifferent: () => void;
  onClose: () => void;
}) {
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function use() {
    if (!ack || busy) return;
    setBusy(true);
    setError(null);
    try {
      await useExistingCardForBidding();
      await onUseExisting();
    } catch (err: any) {
      setError(err?.message ?? 'Could not use that card. Try a different one.');
      setBusy(false);
    }
  }

  return (
    <div className="addcard__form">
      <div className="addcard__savedcard">
        <span className="addcard__savedcard-label">Card on file</span>
        <strong>{formatCard(card.brand, card.last4)}</strong>
      </div>

      <label className="addcard__ack">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} aria-label="Acknowledge auction charge" />
        <span>{ACK_TEXT}</span>
      </label>

      {error && <p className="addcard__error" role="alert">{error}</p>}

      <div className="addcard__actions">
        <button type="button" className="btn btn--ghost btn--md" onClick={onDifferent} disabled={busy}>
          Use a different card
        </button>
        <button type="button" className="btn btn--primary btn--md" onClick={use} disabled={!ack || busy}>
          {busy ? 'Enabling…' : 'Use this card'}
        </button>
      </div>
      <button type="button" className="addcard__cancel-link" onClick={onClose} disabled={busy}>Cancel</button>
    </div>
  );
}

function CardForm({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> }) {
  const stripe = useStripe();
  const elements = useElements();
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || !ack || busy) return;
    setBusy(true);
    setError(null);

    const { error: confirmErr } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required', // stay in-app; SCA pops a modal only if needed
      confirmParams: { return_url: window.location.href },
    });

    if (confirmErr) {
      setError(confirmErr.message ?? 'That card could not be saved. Try another.');
      setBusy(false);
      return;
    }

    setConfirming(true);
    await onDone();
  }

  return (
    <form className="addcard__form" onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />

      <label className="addcard__ack">
        <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} aria-label="Acknowledge auction charge" />
        <span>{ACK_TEXT}</span>
      </label>

      {error && <p className="addcard__error" role="alert">{error}</p>}

      <div className="addcard__actions">
        <button type="button" className="btn btn--ghost btn--md" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="submit" className="btn btn--primary btn--md" disabled={!stripe || !ack || busy}>
          {confirming ? 'Finishing…' : busy ? 'Saving…' : 'Save card'}
        </button>
      </div>
    </form>
  );
}

function formatCard(brand: string | null, last4: string | null): string {
  const b = brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card';
  return `${b} ···· ${last4 ?? '••••'}`;
}
